
import { useState } from 'react';
import { toast } from 'sonner';
import Button from '../components/Button';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    inquiryType: '',
    message: '',
    name: '',
    email: '',
  });

  const [isHoveringPowerSend, setIsHoveringPowerSend] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Message sent successfully! We'll get back to you soon.");
    setFormData({
      title: '',
      inquiryType: '',
      message: '',
      name: '',
      email: '',
    });
  };

  const handlePowerSend = () => {
    toast.success("Your message has been prioritized! We'll respond very soon.");
    setFormData({
      title: '',
      inquiryType: '',
      message: '',
      name: '',
      email: '',
    });
  };

  return (
    <div 
      className="min-h-screen bg-black" 
      style={{ 
        backgroundImage: "url('/lovable-uploads/f9626e62-6c76-4ca8-abbd-7f4a7e169a28.png')",
        backgroundSize: '100% auto', 
        backgroundPosition: 'top center', 
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'scroll'
      }}
    >
      <div className="bg-black bg-opacity-75 text-white py-16">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-bold">Contact Us</h1>
          <p className="text-xl mt-4 text-gray-300">
            Get in touch with our team
          </p>
        </div>
      </div>

      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="bg-black bg-opacity-80 p-8 rounded-lg text-white">
            <h2 className="section-title text-white after:bg-omoduduwa-terracotta">Reach Out</h2>
            <p className="mb-6 text-gray-300">
              Whether you have questions about our initiatives, want to get involved, 
              or need support with a community issue, we're here to help.
            </p>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">Office Location</h3>
                <p className="text-gray-300">
                  123 Civic Center Road<br />
                  Lagos, Nigeria
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">Contact Information</h3>
                <p className="text-gray-300">
                  <strong className="text-omoduduwa-terracotta">Email:</strong> omooduduwa401@gmail.com<br />
                  <strong className="text-omoduduwa-terracotta">Phone:</strong> 09134648310<br />
                  <strong className="text-omoduduwa-terracotta">Hours:</strong> Monday-Friday, 9am-5pm
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">Social Media</h3>
                <div className="flex space-x-4">
                  <a href="https://omooduduwa.org" className="text-white hover:text-omoduduwa-terracotta transition-colors">
                    🌍 Join
                  </a>
                  <a href="https://x.com/i/communities/" className="text-white hover:text-omoduduwa-terracotta transition-colors">
                    X Community
                  </a>
                  <a href="https://tiktok.com/@omooduduwa.or" className="text-white hover:text-omoduduwa-terracotta transition-colors">
                    TikTok
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-black bg-opacity-80 p-8 rounded-lg text-white">
            <h2 className="section-title text-white after:bg-omoduduwa-terracotta">Send a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block mb-1 font-medium text-white">
                  Your Name
                </label>
                <input 
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-black bg-opacity-40 border border-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-omoduduwa-terracotta text-white"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block mb-1 font-medium text-white">
                  Email Address
                </label>
                <input 
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-black bg-opacity-40 border border-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-omoduduwa-terracotta text-white"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="title" className="block mb-1 font-medium text-white">
                  Title
                </label>
                <input 
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-black bg-opacity-40 border border-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-omoduduwa-terracotta text-white"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="inquiryType" className="block mb-1 font-medium text-white">
                  Inquiry/Problem Type
                </label>
                <select 
                  id="inquiryType"
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-black bg-opacity-40 border border-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-omoduduwa-terracotta text-white"
                  required
                >
                  <option value="" className="bg-gray-900">Select an option</option>
                  <option value="General Inquiry" className="bg-gray-900">General Inquiry</option>
                  <option value="Membership Question" className="bg-gray-900">Membership Question</option>
                  <option value="Volunteer Opportunity" className="bg-gray-900">Volunteer Opportunity</option>
                  <option value="Partnership Proposal" className="bg-gray-900">Partnership Proposal</option>
                  <option value="Community Issue" className="bg-gray-900">Community Issue</option>
                  <option value="Other" className="bg-gray-900">Other</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="message" className="block mb-1 font-medium text-white">
                  Message
                </label>
                <textarea 
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-2 bg-black bg-opacity-40 border border-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-omoduduwa-terracotta text-white"
                  required
                ></textarea>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button type="submit" variant="primary" size="lg" className="hover:bg-omoduduwa-terracotta/80">
                  Send Message
                </Button>
                
                <div className="relative">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="lg"
                    onClick={handlePowerSend}
                    onMouseEnter={() => setIsHoveringPowerSend(true)}
                    onMouseLeave={() => setIsHoveringPowerSend(false)}
                    className="border-omoduduwa-terracotta text-white hover:bg-omoduduwa-terracotta"
                  >
                    PowerSend
                  </Button>
                  
                  {isHoveringPowerSend && (
                    <div className="absolute bottom-full left-0 mb-2 w-64 bg-black text-white p-2 rounded text-sm">
                      Send with ₦5000 to be prioritized.
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
